import {
  CaretLeftIcon,
  CaretRightIcon,
  ChatCircleIcon,
  CircleNotchIcon,
  CodeIcon,
  GlobeXIcon,
  HeartIcon,
  PlayIcon,
  StopIcon,
  WarningDiamondIcon,
} from '@phosphor-icons/react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { Track } from './api.ts'
import { Link } from './Router.tsx'
import { ScrollingVisualizer } from './ScrollingVisualizer.tsx'
import { Spinner } from './Spinner.tsx'

interface TracksListProps {
  tracks: Track[]
  likedTracks: Track[]
  user: { id: string } | null
  loadingTracks: boolean
  loadingLiked: boolean
  loadingMoreTracks: boolean
  loadingMoreLiked: boolean
  hasMoreTracks: boolean
  hasMoreLikedTracks: boolean
  sidebarTab: 'compiled' | 'tracks' | 'liked'
  activeSidebarTrackId: string | null
  playingSidebarTrackId: string | null
  isPlaying: boolean
  channelMode: 'single' | 'dual'
  likedTrackIds: Set<string>
  trackErrors: Map<string, string | null>
  onPlaySidebarTrack: (track: Track) => void
  onStopSidebarTrack: () => void
  onLoadTrack: (track: Track) => void
  onLoadInA: (track: Track) => void
  onLoadInB: (track: Track) => void
  onToggleLike: (track: Track) => void
  onShowAuthModal: () => void
  onLoadMoreTracks: () => void
  onLoadMoreLiked: () => void
  hasNoAccount: boolean
}

export const TracksList = memo(
  ({
    tracks,
    likedTracks,
    user,
    loadingTracks,
    loadingLiked,
    loadingMoreTracks,
    loadingMoreLiked,
    hasMoreTracks,
    hasMoreLikedTracks,
    sidebarTab,
    activeSidebarTrackId,
    playingSidebarTrackId,
    isPlaying,
    channelMode,
    likedTrackIds,
    trackErrors,
    onPlaySidebarTrack,
    onStopSidebarTrack,
    onLoadTrack,
    onLoadInA,
    onLoadInB,
    onToggleLike,
    onShowAuthModal,
    onLoadMoreTracks,
    onLoadMoreLiked,
    hasNoAccount,
  }: TracksListProps) => {
    const [hasInitialLoad, setHasInitialLoad] = useState(false)
    const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null)
    const [loadedTrackIds, setLoadedTrackIds] = useState<Set<string>>(new Set())
    const hasInitialLoadRef = useRef(false)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const loaderElementRef = useRef<HTMLDivElement | null>(null)
    const onLoadMoreTracksRef = useRef(onLoadMoreTracks)
    const onLoadMoreLikedRef = useRef(onLoadMoreLiked)
    const hasMoreTracksRef = useRef(hasMoreTracks)
    const hasMoreLikedTracksRef = useRef(hasMoreLikedTracks)
    const loadingMoreTracksRef = useRef(loadingMoreTracks)
    const loadingMoreLikedRef = useRef(loadingMoreLiked)
    const isTriggeredRef = useRef(false)

    useEffect(() => {
      onLoadMoreTracksRef.current = onLoadMoreTracks
    }, [onLoadMoreTracks])

    useEffect(() => {
      onLoadMoreLikedRef.current = onLoadMoreLiked
    }, [onLoadMoreLiked])

    useEffect(() => {
      hasMoreTracksRef.current = hasMoreTracks
    }, [hasMoreTracks])

    useEffect(() => {
      hasMoreLikedTracksRef.current = hasMoreLikedTracks
    }, [hasMoreLikedTracks])

    useEffect(() => {
      loadingMoreTracksRef.current = loadingMoreTracks
      if (!loadingMoreTracks) {
        isTriggeredRef.current = false
      }
    }, [loadingMoreTracks])

    useEffect(() => {
      loadingMoreLikedRef.current = loadingMoreLiked
      if (!loadingMoreLiked) {
        isTriggeredRef.current = false
      }
    }, [loadingMoreLiked])

    useEffect(() => {
      setHasInitialLoad(false)
      hasInitialLoadRef.current = false
    }, [sidebarTab])

    useEffect(() => {
      const isLoading = sidebarTab === 'liked' ? loadingLiked : loadingTracks
      if (!isLoading) {
        setHasInitialLoad(true)
        hasInitialLoadRef.current = true
      }
    }, [loadingTracks, loadingLiked, sidebarTab])

    const scrollContainerRef = useRef<HTMLElement | null>(null)

    const loaderRef = useCallback(
      (element: HTMLDivElement | null) => {
        if (loaderElementRef.current && observerRef.current) {
          observerRef.current.unobserve(loaderElementRef.current)
        }

        loaderElementRef.current = element

        if (!element) {
          return
        }

        const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
          let current: HTMLElement | null = el.parentElement
          while (current) {
            const style = window.getComputedStyle(current)
            if (
              style.overflowY === 'auto'
              || style.overflowY === 'scroll'
              || style.overflow === 'auto'
              || style.overflow === 'scroll'
            ) {
              return current
            }
            current = current.parentElement
          }
          return null
        }

        const scrollContainer = findScrollContainer(element)
        scrollContainerRef.current = scrollContainer

        if (observerRef.current) {
          observerRef.current.disconnect()
        }

        observerRef.current = new IntersectionObserver(
          entries => {
            const entry = entries[0]
            if (!hasInitialLoadRef.current) return
            const hasMore = sidebarTab === 'liked' ? hasMoreLikedTracksRef.current : hasMoreTracksRef.current
            const loadingMore = sidebarTab === 'liked' ? loadingMoreLikedRef.current : loadingMoreTracksRef.current
            const onLoadMore = sidebarTab === 'liked' ? onLoadMoreLikedRef.current : onLoadMoreTracksRef.current
            if (
              entry.isIntersecting
              && hasMore
              && !loadingMore
              && !isTriggeredRef.current
              && onLoadMore
            ) {
              isTriggeredRef.current = true
              onLoadMore()
            }
          },
          { rootMargin: '200px', root: scrollContainer },
        )

        observerRef.current.observe(element)
      },
      [sidebarTab],
    )

    useEffect(() => {
      if (hasInitialLoad && loaderElementRef.current && observerRef.current) {
        observerRef.current.observe(loaderElementRef.current)
      }
    }, [hasInitialLoad])

    useEffect(() => {
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect()
          observerRef.current = null
        }
      }
    }, [])

    useEffect(() => {
      // Clear loaded state when playing track changes
      if (playingSidebarTrackId) {
        setLoadedTrackIds(prev => {
          const next = new Set(prev)
          // Clear all except the currently playing track
          next.clear()
          return next
        })
      }
      else {
        // Clear all when stopped
        setLoadedTrackIds(new Set())
      }
    }, [playingSidebarTrackId])

    const handlePlayTrack = useCallback(async (track: Track) => {
      setLoadingTrackId(track.id)
      setLoadedTrackIds(prev => {
        const next = new Set(prev)
        next.delete(track.id)
        return next
      })
      try {
        await onPlaySidebarTrack(track)
        setLoadedTrackIds(prev => new Set(prev).add(track.id))
      }
      catch (err) {
        // Error handled by parent
      }
      finally {
        setLoadingTrackId(null)
      }
    }, [onPlaySidebarTrack])

    // Render liked tracks
    if (sidebarTab === 'liked') {
      return (
        <div className="flex-1 flex flex-col overflow-hidden bg-black">
          <div
            className={`flex-1 overflow-auto px-2 ${!user ? 'flex items-center justify-center' : ''}`}
          >
            {!user
              ? (
                <button
                  onClick={onShowAuthModal}
                  className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:opacity-90 flex items-center gap-2"
                >
                  Sign In
                </button>
              )
              : loadingLiked
              ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-5 h-5 text-[#888]">
                    <Spinner />
                  </div>
                </div>
              )
              : likedTracks.length === 0
              ? <div className="text-center py-8 text-[#888] text-sm">No liked loops yet</div>
              : (
                <div className="">
                  {likedTracks.map(track => (
                    <div key={track.id} className="border-b border-b-[#333] px-3 py-3 relative">
                      <ScrollingVisualizer
                        isPlaying={loadedTrackIds.has(track.id) && playingSidebarTrackId === track.id && activeSidebarTrackId === track.id}
                        className="absolute inset-0 w-full h-full pointer-events-none opacity-40"
                        scrollStep={2}
                      />
                      <div className="flex items-start justify-between gap-2 relative z-10">
                        <div className="flex flex-1 justify-between min-w-0">
                          <div className="flex gap-2 flex-1 min-w-0 overflow-hidden">
                            {loadingTrackId === track.id
                              ? (
                                <button
                                  disabled
                                  className="px-2 py-1 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded text-xs flex items-center shrink-0 opacity-90"
                                >
                                  <CircleNotchIcon weight="regular" size={12} className="animate-spin" />
                                </button>
                              )
                              : activeSidebarTrackId === track.id && isPlaying
                              ? (
                                <button
                                  onPointerDown={onStopSidebarTrack}
                                  className="px-2 py-1 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:bg-neutral-800 text-xs flex items-center shrink-0"
                                >
                                  <StopIcon weight="fill" size={12} />
                                </button>
                              )
                              : trackErrors.get(track.id)
                              ? (
                                <button
                                  disabled
                                  className="px-2 py-1 bg-transparent text-red-500 rounded cursor-not-allowed text-xs flex items-center shrink-0"
                                  title={`Loop has error: ${trackErrors.get(track.id)}`}
                                >
                                  <WarningDiamondIcon weight="regular" size={12} />
                                </button>
                              )
                              : (
                                <button
                                  onPointerDown={() => handlePlayTrack(track)}
                                  className="px-2 py-1 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 text-xs flex items-center shrink-0"
                                >
                                  <PlayIcon weight="fill" size={12} />
                                </button>
                              )}
                            <div className="font-semibold text-sm flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                              <span
                                className="truncate"
                                title={`${track.userName || 'Unknown'} - ${track.title}`}
                              >
                                <span className={trackErrors.get(track.id) ? 'text-red-500' : 'text-[#888]'}>
                                  <Link
                                    href={`/profile/${track.userId}`}
                                    className={`hover:text-orange-500 cursor-pointer ${
                                      trackErrors.get(track.id) ? 'text-red-500' : ''
                                    }`}
                                  >
                                    {track.userName || 'Unknown'}
                                  </Link>{' '}
                                  -{' '}
                                </span>
                                <Link
                                  href={`/loop/${track.id}`}
                                  className={`hover:text-orange-500 cursor-pointer ${
                                    trackErrors.get(track.id) ? 'text-red-500' : ''
                                  }`}
                                >
                                  {track.title}
                                </Link>
                              </span>
                            </div>
                            <div className="text-xs text-[#888] flex items-center gap-2 shrink-0 mr-2">
                              {track.likesCount > 0 && (
                                <span className="flex items-center gap-1">
                                  <HeartIcon weight="fill" size={12} />
                                  <span>{track.likesCount}</span>
                                </span>
                              )}
                              {track.commentsCount > 0 && (
                                <span className="flex items-center gap-1">
                                  <ChatCircleIcon weight="fill" size={12} />
                                  <span>{track.commentsCount}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => onToggleLike(track)}
                              className={`px-2 py-1 bg-gradient-to-br from-neutral-700 to-neutral-900 rounded hover:opacity-90 text-xs flex items-center ${
                                likedTrackIds.has(track.id) ? 'text-red-500' : 'text-white'
                              }`}
                              title={likedTrackIds.has(track.id) ? 'Unlike' : 'Like'}
                            >
                              {likedTrackIds.has(track.id)
                                ? <HeartIcon weight="fill" size={12} className="text-red-500" />
                                : <HeartIcon weight="fill" size={12} className="text-neutral-600" />}
                            </button>
                            <div className="flex gap-1">
                              {channelMode === 'dual'
                                ? (
                                  <>
                                    <button
                                      onClick={() => onLoadInA(track)}
                                      className="pr-2.5 pl-1.5 rounded flex items-center justify-center gap-0 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white hover:opacity-90"
                                      title="Load in A"
                                    >
                                      <CaretLeftIcon weight="regular" size={12} />
                                      <span className="text-[8px] leading-none -ml-0.5 relative top-[1px]">
                                        A
                                      </span>
                                    </button>
                                    <button
                                      onClick={() => onLoadInB(track)}
                                      className="pl-2.5 pr-1.5 py-1 rounded flex items-center justify-center gap-0 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white hover:opacity-90"
                                      title="Load in B"
                                    >
                                      <span className="text-[8px] leading-none -mr-0.5 relative top-[1px]">
                                        B
                                      </span>
                                      <CaretRightIcon weight="regular" size={12} />
                                    </button>
                                  </>
                                )
                                : (
                                  <button
                                    onClick={() => onLoadTrack(track)}
                                    className="px-2 py-1 rounded text-xs flex items-center bg-gradient-to-br from-neutral-500 to-neutral-700 text-white hover:opacity-90"
                                    title="Load loop"
                                  >
                                    <CaretLeftIcon weight="regular" size={12} />
                                  </button>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {hasInitialLoad && (
                    <>
                      {hasMoreLikedTracks && (
                        <div
                          ref={loaderRef}
                          className="border-b border-b-[#333] p-3 text-center text-[#888] flex items-center justify-center"
                        >
                          {loadingMoreLiked
                            ? (
                              <div className="w-5 h-5">
                                <Spinner />
                              </div>
                            )
                            : <div style={{ height: '1px', width: '100%' }} />}
                        </div>
                      )}
                      {!hasMoreLikedTracks && !loadingMoreLiked && likedTracks.length > 0 && (
                        <div className="text-center pt-4 pb-5 text-[#888] flex items-center justify-center gap-3">
                          <CodeIcon size={20} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
          </div>
        </div>
      )
    }

    // Render tracks list
    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-black">
        <div
          className={`flex-1 overflow-auto px-2 ${!user ? 'flex items-center justify-center' : ''}`}
        >
          {!user
            ? (
              <button
                onClick={onShowAuthModal}
                className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:opacity-90 flex items-center gap-2"
              >
                Sign In
              </button>
            )
            : loadingTracks
            ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-5 h-5 text-[#888]">
                  <Spinner />
                </div>
              </div>
            )
            : tracks.length === 0
            ? <div className="text-center py-8 text-[#888] text-sm">No loops saved yet</div>
            : (
              <div className="">
                {tracks.map(track => (
                  <div key={track.id} className="border-b border-b-[#333] px-3 py-3 relative">
                    <ScrollingVisualizer
                      isPlaying={loadedTrackIds.has(track.id) && playingSidebarTrackId === track.id}
                      className="absolute inset-0 w-full h-full pointer-events-none opacity-40"
                      scrollStep={2}
                    />
                    <div className="flex items-start justify-between gap-2 relative z-10">
                      <div className="flex flex-1 justify-between min-w-0">
                        <div className="flex gap-2 flex-1 min-w-0 overflow-hidden">
                          {loadingTrackId === track.id
                            ? (
                              <button
                                disabled
                                className="px-2 py-1 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded text-xs flex items-center shrink-0 opacity-90"
                              >
                                <CircleNotchIcon weight="regular" size={12} className="animate-spin" />
                              </button>
                            )
                            : activeSidebarTrackId === track.id && isPlaying
                            ? (
                              <button
                                onPointerDown={onStopSidebarTrack}
                                className="px-2 py-1 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:bg-neutral-800 text-xs flex items-center shrink-0"
                              >
                                <StopIcon weight="fill" size={12} />
                              </button>
                            )
                            : trackErrors.get(track.id)
                            ? (
                              <button
                                disabled
                                className="px-2 py-1 bg-transparent text-red-500 rounded cursor-not-allowed text-xs flex items-center shrink-0"
                                title={`Track has error: ${trackErrors.get(track.id)}`}
                              >
                                <WarningDiamondIcon weight="regular" size={12} />
                              </button>
                            )
                            : (
                              <button
                                onPointerDown={() => handlePlayTrack(track)}
                                className="px-2 py-1 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 text-xs flex items-center shrink-0"
                              >
                                <PlayIcon weight="fill" size={12} />
                              </button>
                            )}
                          <div className="font-semibold text-sm flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                            <span className="truncate" title={track.title}>
                              <Link
                                href={`/loop/${track.id}`}
                                className={`hover:text-orange-500 cursor-pointer ${
                                  trackErrors.get(track.id) ? 'text-red-500' : ''
                                }`}
                              >
                                {track.title}
                              </Link>
                            </span>
                          </div>
                          <div className="text-xs text-[#888] flex items-center gap-2 shrink-0 mr-2">
                            {track.likesCount > 0 && (
                              <span className="flex items-center gap-1">
                                <HeartIcon weight="fill" size={12} />
                                <span>{track.likesCount}</span>
                              </span>
                            )}
                            {track.commentsCount > 0 && (
                              <span className="flex items-center gap-1">
                                <ChatCircleIcon weight="fill" size={12} />
                                <span>{track.commentsCount}</span>
                              </span>
                            )}
                            {!track.isPublic && (
                              <span className="flex items-center gap-1">
                                <GlobeXIcon weight="regular" size={12} />
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="hidden md:flex gap-1 shrink-0">
                          {channelMode === 'dual'
                            ? (
                              <>
                                <button
                                  onClick={() => onLoadInA(track)}
                                  className="pr-2.5 pl-1.5 rounded flex items-center justify-center gap-0 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white hover:opacity-90"
                                  title="Load in A"
                                >
                                  <CaretLeftIcon weight="regular" size={12} />
                                  <span className="text-[8px] leading-none -ml-0.5 relative top-[1px]">
                                    A
                                  </span>
                                </button>
                                <button
                                  onClick={() => onLoadInB(track)}
                                  className="pl-2.5 pr-1.5 rounded flex items-center justify-center gap-0 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white hover:opacity-90"
                                  title="Load in B"
                                >
                                  <span className="text-[8px] leading-none -mr-0.5 relative top-[1px]">
                                    B
                                  </span>
                                  <CaretRightIcon weight="regular" size={12} />
                                </button>
                              </>
                            )
                            : (
                              <button
                                onClick={() => onLoadTrack(track)}
                                className="px-2 py-1 rounded text-xs flex items-center bg-gradient-to-br from-neutral-500 to-neutral-700 text-white hover:opacity-90"
                                title="Load loop"
                              >
                                <CaretLeftIcon weight="regular" size={12} />
                              </button>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {hasInitialLoad && (
                  <>
                    {hasMoreTracks && (
                      <div
                        ref={loaderRef}
                        className="border-b border-b-[#333] p-4 text-center text-[#888] flex items-center justify-center"
                      >
                        <div className="w-5 h-5">
                          <Spinner />
                        </div>
                      </div>
                    )}
                    {!hasMoreTracks && !loadingMoreTracks && tracks.length > 0 && (
                      <div className="text-center pt-4 pb-5 text-[#888] flex items-center justify-center gap-3">
                        <CodeIcon size={20} />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Only rerender if these props actually change
    return (
      prevProps.tracks === nextProps.tracks
      && prevProps.likedTracks === nextProps.likedTracks
      && prevProps.user === nextProps.user
      && prevProps.loadingTracks === nextProps.loadingTracks
      && prevProps.loadingLiked === nextProps.loadingLiked
      && prevProps.loadingMoreTracks === nextProps.loadingMoreTracks
      && prevProps.loadingMoreLiked === nextProps.loadingMoreLiked
      && prevProps.hasMoreTracks === nextProps.hasMoreTracks
      && prevProps.hasMoreLikedTracks === nextProps.hasMoreLikedTracks
      && prevProps.sidebarTab === nextProps.sidebarTab
      && prevProps.activeSidebarTrackId === nextProps.activeSidebarTrackId
      && prevProps.playingSidebarTrackId === nextProps.playingSidebarTrackId
      && prevProps.isPlaying === nextProps.isPlaying
      && prevProps.channelMode === nextProps.channelMode
      && prevProps.likedTrackIds === nextProps.likedTrackIds
      && prevProps.trackErrors === nextProps.trackErrors
    )
  },
)

TracksList.displayName = 'TracksList'
